// A pagination item is either a page number to render as a button, or an
// ellipsis gap. Left/right ellipses are distinguished so React keys stay stable
// when both are present.
export type PaginationItem = number | 'ellipsis-left' | 'ellipsis-right'

const range = (start: number, end: number): number[] =>
  Array.from({ length: Math.max(0, end - start + 1) }, (_, i) => start + i)

// Build a windowed list of pagination items: always show the first and last
// page, the current page with `siblingCount` neighbours either side, and an
// ellipsis where there is a gap. Prevents rendering hundreds of buttons (which
// overflowed the list card) while keeping first/last reachable in one click.
//
// `siblingCount` is the number of pages shown either side of the current page.
export const getPaginationItems = (
  currentPage: number,
  totalPages: number,
  siblingCount = 1
): PaginationItem[] => {
  // first + last + current + 2*siblings + 2 ellipsis slots. When everything
  // fits in that budget there is no point hiding anything — show every page.
  const maxVisible = siblingCount * 2 + 5
  if (totalPages <= maxVisible) {
    return range(1, totalPages)
  }

  const leftSibling = Math.max(currentPage - siblingCount, 1)
  const rightSibling = Math.min(currentPage + siblingCount, totalPages)

  // Show an ellipsis only when it actually replaces 2+ pages; otherwise the
  // gap is a single page, so render the page itself instead of a "…".
  const showLeftEllipsis = leftSibling > 2
  const showRightEllipsis = rightSibling < totalPages - 1

  // The fixed count of pages shown on the collapsed side (1 edge + current
  // block) so the window width stays constant as the user pages through.
  const edgeBlock = 3 + 2 * siblingCount

  if (!showLeftEllipsis && showRightEllipsis) {
    return [...range(1, edgeBlock), 'ellipsis-right', totalPages]
  }

  if (showLeftEllipsis && !showRightEllipsis) {
    return [
      1,
      'ellipsis-left',
      ...range(totalPages - edgeBlock + 1, totalPages)
    ]
  }

  return [
    1,
    'ellipsis-left',
    ...range(leftSibling, rightSibling),
    'ellipsis-right',
    totalPages
  ]
}
